import classNames from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BackButton from '@swf/components/StarsModal/components/BackButton';
import Body from '@swf/components/StarsModal/components/Body';
import CloseButton from '@swf/components/StarsModal/components/CloseButton';
import Header from '@swf/components/StarsModal/components/Header/Header';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';

import styles from './NotificationContainer.scss';

export interface Props {
  title?: React.ReactNode;
  backButton?: boolean;
  onBack?: () => void;
  bodyClass?: string;
  hideCloseButton?: boolean;
  onClose?(): void;
  closeRedirect?: string;
  styledNotification?: boolean;
}

const NotificationContainer: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  onClose,
  children,
  onBack,
  backButton = false,
  bodyClass,
  hideCloseButton,
  closeRedirect,
  styledNotification = false
}) => {
  const fromEvent = useFromEvent();
  const headerRef = useRef<HTMLHeadingElement>(null);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const { modal } = useDispatch<Dispatch>();
  const showBodyCloseButton = !isEmbedded && styledNotification;

  useEffect(() => {
    fromEvent(document, 'starsbeforeclose').subscribe((e) => {
      if (!isDesktopEmbedded && onClose) e.preventDefault();
      if (!isDesktopEmbedded && closeRedirect) {
        e.preventDefault();
        modal.replace([{ path: closeRedirect }]);
      }
      onClose?.();
    });
  }, [onClose, fromEvent, isDesktopEmbedded, closeRedirect, modal]);

  useEffect(() => {
    if (isEmbedded && headerRef.current) {
      document.title = headerRef.current.innerText;
    }
  }, [title, isEmbedded]);

  return (
    <>
      {title && (
        <Header className={classNames({ [styles.hidden]: isEmbedded })}>
          {backButton && <BackButton onClick={onBack} />}
          <span ref={headerRef}>{title}</span>
          {!hideCloseButton && <CloseButton href={closeRedirect} onClick={onClose} />}
        </Header>
      )}
      <Body
        className={classNames(bodyClass, styles.modalBody, {
          [styles.styledModal]: styledNotification
        })}
      >
        {showBodyCloseButton && <CloseButton href={closeRedirect} onClick={onClose} />}
        {children}
      </Body>
    </>
  );
};
export default NotificationContainer;
